import { StatusItem } from "@types";
import { Edit } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getInspRules, getInspStatus, getInspScores } from "../../services/inspectService";
import { useTutorial } from "../../context/TutorialContext";
import { Modal, Typography, Box, Divider, Tooltip } from "@mui/material";
import { StatusProvider, useStatusContext } from "../../context/InspStatusContext";
import { formatDate, SF, toQuery } from "../../utils/utils";
import StatusModal from "../../components/new/InspStatus/StatusModal";
import { CustomPagination } from "../../components/customComponent/CustomPagination";
import ScoreBandControl, { computeBand } from "../../components/ScoreBandControl";
import { FilterState, ScoreBandResult, ScoreBandValue } from "../../@types/services/inspStatusService";
import StatusTable from "../../components/new/StatusTable";

export { toQuery };

/** 밴드 → 서버 파라미터. 전체(100%)이거나 분포를 아직 못 받았으면 아무것도 안 붙임 */
export const toBandQuery = (band: ScoreBandValue, r: ScoreBandResult) => {
  if (r.total === 0 || r.count >= r.total) return {};
  return {
    score_min: r.lo,
    score_max: r.hi,
    // 페이지를 넘겨도 "기준값에 가까운 순"을 유지하고 싶을 때만 사용 (아래 주석 참고)
    anchor: band.anchor,
  };
};

export default function InspectionStatusPage() {
  const {
    imageList,
    setImageList,
    page,
    setPage,
    filters,
    setFilters,
    selectedStatus,
    setSelectedStatus,
    setTotalDataCount,
  } = useStatusContext();

  const [totalPages, setTotalPages] = useState(1);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [band, setBand] = useState<ScoreBandValue>({ anchor: 0.5, pct: 7, mode: "near" });
  const [scores, setScores] = useState<number[]>([]);
  const [scoresLoading, setScoresLoading] = useState(false);
  const scoreReq = useRef(0);

  const sortedScores = useMemo(() => [...scores].sort((a, b) => a - b), [scores]);
  const bandResult = useMemo(() => computeBand(sortedScores, band.anchor, band.pct, band.mode), [sortedScores, band]);

  // 모달 전용 상태 (테이블과 완전히 분리)
  const [modalImageList, setModalImageList] = useState<StatusItem[] | null>(null);
  const [modalPage, setModalPage] = useState(1);
  const [openThresholdOption, setOpenThresholdOption] = useState(false);

  const { run, pendingStartRef, nextSubStep, startTour, tutorialKey, stepIndexRef, tutorialKeyRef } = useTutorial();

  useEffect(() => {
    if (pendingStartRef.current) {
      pendingStartRef.current = false;
      startTour(tutorialKeyRef.current);
    }
  }, []);

  const fetchData = async (
    targetPage: number,
    activeFilters = appliedFilters,
    bandQuery: Partial<ReturnType<typeof toBandQuery>> = {},
  ) => {
    console.log("적용 필터", filters);
    const response = await getInspStatus({
      page: targetPage,
      // 빈 문자열은 전송 안 하도록 처리
      ...(activeFilters.name && { name: activeFilters.name }),
      ...(activeFilters.material && {
        material: activeFilters.material,
      }),
      ...(activeFilters.client_ip && { client_ip: activeFilters.client_ip }),
      ...(activeFilters.lot && { lot: activeFilters.lot }),
      ...(activeFilters.label && { label: activeFilters.label }),
      ...(activeFilters.start_date && { start_date: activeFilters.start_date }),
      ...(activeFilters.end_date && { end_date: activeFilters.end_date }),
      ...bandQuery,
    });
    setTotalDataCount(response.data.total_count);
    setImageList(response.data.contents);
    setTotalPages(Math.ceil(response.data.total_count / 20));
  };

  // 경계값 샘플링 패널에서 쓸 score 목록: 현재 텍스트/기간 필터 기준 (score 범위 자체는 제외)
  const fetchScores = useCallback(async (activeFilters: FilterState) => {
    const req = ++scoreReq.current;
    setScoresLoading(true);
    try {
      const res = await getInspScores(toQuery(activeFilters));
      if (req !== scoreReq.current) return;
      const next: number[] = (res?.data?.scores ?? []).filter((n: number) => typeof n === "number" && !Number.isNaN(n));
      setScores(next);
    } finally {
      if (req === scoreReq.current) setScoresLoading(false);
    }
  }, []);

  // 드래그 중엔 컨트롤 미리보기만 갱신. 실제 조회는 무겁기 때문에(수만~수십만 건) "적용" 버튼을 눌러야 나감
  const handleBandChange = useCallback((value: ScoreBandValue) => {
    setBand(value);
  }, []);

  const handleApplyBand = () => {
    setPage(1);
    fetchData(1, appliedFilters, toBandQuery(band, bandResult));
  };

  // 페이지 그룹 계산 (5개씩 묶기)
  const pageGroupStart = Math.floor((page - 1) / 5) * 5 + 1;
  const pageGroupEnd = Math.min(pageGroupStart + 4, totalPages);

  const visiblePages = Array.from({ length: pageGroupEnd - pageGroupStart + 1 }, (_, i) => pageGroupStart + i);

  useEffect(() => {
    console.log(page);
    fetchData(page, appliedFilters, openThresholdOption ? toBandQuery(band, bandResult) : {});
  }, [page]); // page 의존성 추가

  // 최초 진입 시 전체 데이터 기준 score 분포를 받아 경계값 샘플링 컨트롤을 채워둠
  useEffect(() => {
    fetchScores(appliedFilters);
  }, []);

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPage(1);
    fetchData(1, filters, openThresholdOption ? toBandQuery(band, bandResult) : {}); // 적용 필터 + 경계값 샘플링으로 즉시 호출
    fetchScores(filters); // 새 필터 기준으로 경계값 샘플링용 score 분포도 갱신

    if (run) {
      setTimeout(() => nextSubStep(), 500);
    }
  };

  const handleReset = () => {
    const empty = {
      name: "",
      material: "",
      client_ip: "",
      lot: "",
      label: "",
      start_date: "",
      end_date: "",
    };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
    fetchData(1, empty);
    fetchScores(empty);
  };

  const handleOpenStatus = (image: StatusItem) => {
    setSelectedStatus(image);
    setModalImageList(imageList); // 모달 열릴 때 현재 imageList 복사
    setModalPage(page); // 모달 열릴 때 현재 page 복사
    setOpenStatusModal(true);

    if (run) {
      setTimeout(() => nextSubStep(), 100);
    }
  };

  const handleCloseStatus = () => {
    setSelectedStatus(null);
    setOpenStatusModal(false);
  };

  const activeFilterCount = Object.entries(appliedFilters).filter(([, v]) => v !== "").length;

  return (
    <div className="p-8">
      <div id="insp-status" className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900">검사현황</h2>
        </div>
        <div id="status-filter" className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
            {/* name */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Name</label>
              <input
                type="text"
                value={filters.name}
                onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="name 검색"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* material_name */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Material</label>
              <input
                id="status-material-filter-input"
                type="text"
                value={filters.material}
                onChange={(e) => setFilters((f) => ({ ...f, material: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="material 검색"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* client_ip */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Client IP</label>
              <input
                type="text"
                value={filters.client_ip}
                onChange={(e) => setFilters((f) => ({ ...f, client_ip: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="IP 검색"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* lot */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Lot</label>
              <input
                type="text"
                value={filters.lot}
                onChange={(e) => setFilters((f) => ({ ...f, lot: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="lot 검색"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* label */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Label</label>
              <input
                type="text"
                value={filters.label}
                onChange={(e) => setFilters((f) => ({ ...f, label: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="label 검색"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* 날짜 범위 + 버튼 */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">기간 (시작)</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">기간 (종료)</label>
              <input
                type="date"
                value={filters.end_date}
                min={filters.start_date}
                onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2 ml-auto">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
              >
                초기화
              </button>
              <Tooltip title="material 필터 적용 시 활성화됩니다.">
                <button
                  disabled={appliedFilters.material == ""}
                  onClick={() => setOpenThresholdOption(!openThresholdOption)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                >
                  경계값 샘플링
                </button>
              </Tooltip>
              <button
                id="apply-status-filter"
                onClick={handleSearch}
                className="px-5 py-2 text-sm rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition flex items-center gap-1.5"
              >
                검색
                {activeFilterCount > 0 && (
                  <span className="bg-white text-blue-500 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          {openThresholdOption && (
            <Box sx={{ mt: 1 }}>
              <Divider />
              <ScoreBandControl scores={scores} value={band} onChange={handleBandChange} disabled={scoresLoading} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", px: "16px", pb: "12px" }}>
                <button
                  onClick={handleApplyBand}
                  className="px-4 py-2 text-sm rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
                >
                  경계값 샘플링 적용
                </button>
              </Box>
            </Box>
          )}
        </div>

        <div id="status-table" className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <StatusTable items={imageList} page={page} onRowClick={handleOpenStatus} />

          {imageList && imageList.length === 0 && (
            <div className="text-center py-10 text-gray-500">검사된 이미지가 없습니다.</div>
          )}

          {selectedStatus && openStatusModal && <StatusModal open={openStatusModal} onClose={handleCloseStatus} />}
        </div>
        <CustomPagination page={page} totalPages={totalPages} onPageChange={setPage} groupSize={5} />
      </div>
    </div>
  );
}
